import { Sparkle, X } from "@phosphor-icons/react";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ForbiddenError } from "~/api/errors";
import {
  NAVIGATE_AUTO_CONFIDENCE,
  type NavigateTargetKind,
  type RouteProposal,
} from "~/api/opsTypes";
import { useApi } from "~/api/provider";
import {
  useCollections,
  useCreateDigestTask,
  useCreateDocument,
  useCreateNudge,
  useNotify,
  useRouteIntent,
  useSaveDocumentBody,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { CommandProposalCard } from "~/features/command/CommandProposalCard";
import { proposeCommand } from "~/features/command/commandApi";
import { screenContextFor } from "~/features/command/commandModel";
import type { CommandProposal, ScreenContext } from "~/features/command/commandTypes";
import { useTaskizeTray } from "~/features/taskize/useTaskizeTray";
import { Button } from "~/foundation/components/Button";
import { useNavigateTo } from "~/foundation/components/navigation";
import { Text } from "~/foundation/components/Text";
import { depths } from "~/foundation/styles/depths";
import { CURRENT_WORKSPACE, useAbilitiesLookup } from "~/policies/usePolicy";
import { kindLabel, missingWarnings, triggerLabel } from "~/shell/askbar/copy";
import { runEditSection } from "~/shell/askbar/editSection";
import { IntentCard } from "~/shell/askbar/IntentCard";
import { useOpenTarget } from "~/shell/askbar/openTarget";
import { askBarHidden, documentPath, routeIdFor, surfaceFor } from "~/shell/askbar/surface";
import { useShellStore } from "~/shell/store";

/**
 * Ask mode — one place a person types intent in natural language and the system PROPOSES.
 *
 * The seam to ⌘K is two store fields, not a pub/sub module: opening this closes the command
 * bar in the same update, so there is always exactly one palette on screen.
 *
 * Nothing here creates anything without a confirm card, with one deliberate exception:
 * `navigate` at or above 0.8 goes straight there. That is safe because navigating is free and
 * reversible, and because the server's confidence for navigate is a DETERMINISTIC ladder, not
 * a model's opinion — one match scores 0.95/0.85 and a multi-match never exceeds 0.9 on an
 * exact top hit and is 0.55 otherwise. So a single fuzzy match auto-navigates and an ambiguous
 * one always asks. That is the product rule, and it is worth keeping.
 *
 * A navigate target is opened by the `path` the server resolved (S3) — a document, a research
 * experiment, a collection or a work item (S4, `/work/{KEY}`) — never by a document route
 * rebuilt from its id.
 */
export function AskBar() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigateTo();
  const openTarget = useOpenTarget();
  const abilities = useAbilitiesLookup();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });

  const open = useShellStore((state) => state.askBarOpen);
  const prefill = useShellStore((state) => state.askBarPrefill);
  const closeAskBar = useShellStore((state) => state.closeAskBar);
  const activeEntityId = useShellStore((state) => state.activeEntityId);
  const requestTaskize = useTaskizeTray((state) => state.requestTaskize);

  const [prompt, setPrompt] = useState("");
  const [proposal, setProposal] = useState<RouteProposal | null>(null);
  // The work command (2026-10-21): "close PLAT-7", "close all items about deployment", "create a work item for X in
  // Y" — proposed by the small model, approved on the card. On a document or collection the document intents go
  // first and the command is the fallback; everywhere else the command goes first and the document intents follow.
  const [command, setCommand] = useState<CommandProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const routeIntent = useRouteIntent();
  const createDigest = useCreateDigestTask();
  const createNudge = useCreateNudge();
  const createDocument = useCreateDocument();
  const notify = useNotify();
  const saveBody = useSaveDocumentBody();
  const { data: collections } = useCollections();

  useEffect(() => {
    if (open) {
      setPrompt(prefill);
      setProposal(null);
      setCommand(null);
      // Biome bans the autoFocus attribute; focus belongs in an effect, after the paint.
      const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [open, prefill]);

  if (!open || askBarHidden(pathname)) {
    return null;
  }

  const surface = surfaceFor(pathname);
  const routeId = routeIdFor(surface, activeEntityId, pathname);
  const canBroadcast = abilities(CURRENT_WORKSPACE)[ABILITY.workspaceUpdate] === true;

  const tryCommand = async (text: string, context: ScreenContext, onOther: () => void) => {
    setBusy(true);
    try {
      const next = await proposeCommand(api, text, context);
      if (next.kind === "other") {
        onOther();
        return;
      }
      setCommand(next);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message ? error.message : t("Couldn't understand that."),
      );
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    const text = prompt.trim();
    if (!text) {
      return;
    }
    setProposal(null);
    setCommand(null);
    const context = screenContextFor(pathname, searchStr);
    const documentFirst = surface === "document" || surface === "collection";
    const routeArgs = { prompt: text, surface, ...(routeId ? { routeId } : {}) };
    if (documentFirst) {
      routeIntent.mutate(routeArgs, {
        onSuccess: (result) => {
          if (!result.actionId) {
            void tryCommand(text, context, () =>
              toast.message(result.message ?? t("I can't do that from here yet.")),
            );
            return;
          }
          handleRoute(result);
        },
        onError: routeError,
      });
      return;
    }
    void tryCommand(text, context, () =>
      routeIntent.mutate(routeArgs, { onSuccess: handleRoute, onError: routeError }),
    );
  };

  const handleRoute = (result: RouteProposal) => {
    if (!result.actionId) {
      toast.message(result.message ?? t("I can't do that from here yet."));
      return;
    }
    if (result.actionId === "taskize") {
      if (!activeEntityId) {
        toast.error(t("Open a document first."));
        return;
      }
      // The tray is the confirm; there is no card for this intent.
      requestTaskize(activeEntityId);
      closeAskBar();
      setPrompt("");
      return;
    }
    if (result.actionId === "navigate") {
      if (!result.document) {
        toast.message(t("I couldn't find a document matching that."));
        return;
      }
      if (result.confidence >= NAVIGATE_AUTO_CONFIDENCE) {
        openTarget(result.document);
        closeAskBar();
        setPrompt("");
        return;
      }
    }
    setProposal(result);
  };
  const routeError = (error: unknown) => {
    if (error instanceof ForbiddenError) {
      toast.error(error.message || t("You don't have permission to do that."));
      return;
    }
    toast.error(t("Couldn't understand that. Try naming a topic, sources, and how often."));
  };

  const warnings = proposal ? missingWarnings(proposal.missing, proposal.actionId, t) : [];
  const canCreate = proposal ? proposal.missing.length === 0 : false;
  const discard = () => setProposal(null);
  const done = () => {
    setProposal(null);
    setCommand(null);
    setPrompt("");
    closeAskBar();
  };

  const run = async (work: () => Promise<void>, failure: string) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={t("Close")}
        onClick={closeAskBar}
        className="fixed inset-0 cursor-default bg-backdrop"
        style={{ zIndex: depths.dialogOverlay }}
      />
      <div
        role="dialog"
        aria-label={t("Ask AI")}
        className="slate-pop fixed inset-x-0 top-[18vh] mx-auto flex w-[560px] max-w-[92vw] flex-col overflow-hidden rounded-md border border-divider bg-menu-bg shadow-menu"
        style={{ zIndex: depths.dialog }}
      >
        <div className="flex items-center gap-2 px-3">
          <Sparkle size={16} aria-hidden className="shrink-0 text-accent" />
          <input
            ref={inputRef}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closeAskBar();
              }
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder={t(
              'Ask anything — "close PLAT-7", "create a work item for … in …", "switch to document X", "digest AI news daily"',
            )}
            aria-label={t("Ask AI")}
            className="h-12 flex-1 bg-transparent text-body text-text outline-none"
          />
          <Button
            variant="primary"
            size="sm"
            disabled={routeIntent.isPending || busy || prompt.trim() === ""}
            onClick={submit}
          >
            {routeIntent.isPending || (busy && !command) ? t("Thinking…") : t("Ask")}
          </Button>
          <button
            type="button"
            aria-label={t("Close")}
            onClick={closeAskBar}
            className="flex size-7 shrink-0 items-center justify-center rounded-sm text-text-secondary hover:bg-hover-bg"
          >
            <X size={15} aria-hidden />
          </button>
        </div>

        {command ? (
          <CommandProposalCard
            proposal={command}
            onDone={done}
            onDiscard={() => setCommand(null)}
          />
        ) : null}

        {proposal?.actionId === "create-digest" ? (
          <IntentCard
            title={proposal.summary ?? t("Create this digest?")}
            meta={[
              `“${proposal.digest?.topic ?? proposal.title ?? ""}”`,
              triggerLabel(proposal.trigger, t),
              proposal.digest?.targetDocumentTitle
                ? `→ ${proposal.digest.targetDocumentTitle}`
                : null,
              proposal.digest?.notifyInApp === false ? null : t("notify in-app"),
            ]
              .filter(Boolean)
              .join(" · ")}
            warnings={warnings}
            warningAction={
              <Button variant="ghost" size="sm" onClick={() => navigate("/settings/task-center")}>
                {t("Open Task Center")}
              </Button>
            }
            primaryLabel={t("Create task")}
            canCreate={canCreate}
            busy={busy}
            onDiscard={discard}
            onPrimary={() =>
              void run(async () => {
                const digest = proposal.digest;
                const target = digest?.targetDocumentId;
                if (!digest || !target) {
                  return;
                }
                await createDigest.mutateAsync({
                  title: proposal.title ?? digest.topic ?? t("Digest"),
                  topic: digest.topic ?? "",
                  sourceMode:
                    digest.sourceMode === "url" || digest.sourceMode === "rss"
                      ? digest.sourceMode
                      : "search",
                  sources: digest.sources,
                  ...(digest.thinkerId ? { thinkerId: digest.thinkerId } : {}),
                  targetDocumentId: target,
                  outputMode: digest.outputMode === "replace" ? "replace" : "prepend",
                  notifyInApp: digest.notifyInApp !== false,
                  trigger: proposal.trigger ?? { kind: "every", everyMinutes: 1440 },
                });
                toast.success(t("Task created"));
                done();
              }, t("Couldn't create that task"))
            }
          />
        ) : null}

        {proposal?.actionId === "create-nudge" ? (
          <IntentCard
            title={proposal.summary ?? t("Set this reminder?")}
            meta={[
              t("Remind about this document"),
              proposal.nudge.whenUtc
                ? new Date(proposal.nudge.whenUtc).toLocaleString()
                : t("no time yet"),
              proposal.nudge.recurrenceRule?.replace("FREQ=", "").toLowerCase() ?? null,
              proposal.nudge.note ? `“${proposal.nudge.note}”` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            warnings={warnings}
            primaryLabel={t("Set reminder")}
            canCreate={canCreate}
            busy={busy}
            onDiscard={discard}
            onPrimary={() =>
              void run(async () => {
                const { nudge } = proposal;
                if (!nudge.whenUtc || !nudge.documentId) {
                  return;
                }
                await createNudge.mutateAsync({
                  target: { targetType: "document", documentId: nudge.documentId },
                  when: {
                    whenUtc: nudge.whenUtc,
                    timezone: nudge.timezone ?? "UTC",
                    ...(nudge.recurrenceRule ? { recurrenceRule: nudge.recurrenceRule } : {}),
                    ...(nudge.note ? { note: nudge.note } : {}),
                  },
                });
                toast.success(t("Nudge set"));
                done();
              }, t("Couldn't set the nudge"))
            }
          />
        ) : null}

        {proposal?.actionId === "create-document" ? (
          <CreateDocumentCard
            proposal={proposal}
            warnings={warnings}
            busy={busy}
            knownCollectionIds={(collections ?? []).map((collection) => collection.id)}
            onDiscard={discard}
            onCreate={(collectionId) =>
              void run(async () => {
                const created = await createDocument.mutateAsync({
                  title: proposal.document.title ?? t("Untitled"),
                  ...(proposal.document.text ? { text: proposal.document.text } : {}),
                  collectionId,
                  publish: true,
                });
                done();
                navigate(documentPath(created.id));
              }, t("Couldn't create that document"))
            }
          />
        ) : null}

        {proposal?.actionId === "navigate" ? (
          <IntentCard
            // The question is chosen by KIND, not taken from `summary`: the server's default
            // summary for navigate is an untranslated "Go to “<title>”" that only repeats the
            // meta line below, so "Open this work item?" would never show in production (S4).
            title={navigateCardTitle(proposal.document?.kind, t)}
            meta={
              proposal.document
                ? `“${proposal.document.title}” · ${kindLabel(proposal.document.kind, t)}`
                : ""
            }
            warnings={warnings}
            primaryLabel={t("Go")}
            canCreate={canCreate && proposal.document !== null}
            busy={busy}
            onDiscard={discard}
            onPrimary={() => {
              if (proposal.document) {
                openTarget(proposal.document);
                done();
              }
            }}
          >
            {proposal.alternates.length > 0 ? (
              <div className="flex flex-col gap-1">
                <Text variant="small">{t("Not it? Maybe one of these:")}</Text>
                <div className="flex flex-wrap gap-1.5">
                  {proposal.alternates.map((alternate) => (
                    <button
                      key={`${alternate.kind}:${alternate.id}`}
                      type="button"
                      onClick={() => {
                        openTarget(alternate);
                        done();
                      }}
                      className="inline-flex h-7 items-center gap-1.5 rounded-full border border-divider bg-bg-secondary px-2.5 text-small text-text hover:border-accent hover:text-accent"
                    >
                      {alternate.title}
                      {/* Decorative: the chip's name is the title; the kind is a hint for the eye. */}
                      <span aria-hidden className="text-text-tertiary">
                        {kindLabel(alternate.kind, t)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </IntentCard>
        ) : null}

        {proposal?.actionId === "edit-section" ? (
          <IntentCard
            title={proposal.summary ?? t("Draft this section?")}
            meta={`## ${proposal.section?.heading ?? ""} · ${t("propose lane")}`}
            preview={proposal.section?.text ?? null}
            warnings={warnings}
            primaryLabel={t("Insert section")}
            canCreate={canCreate && Boolean(proposal.section)}
            busy={busy}
            onDiscard={discard}
            onPrimary={() =>
              void run(async () => {
                const section = proposal.section;
                const documentId = section?.documentId ?? activeEntityId;
                if (!section || !documentId) {
                  toast.error(t("Open a document first."));
                  return;
                }
                const result = await runEditSection({
                  api,
                  documentId,
                  heading: section.heading,
                  body: section.text ?? "",
                  saveBody: (markdown) => saveBody.mutateAsync({ documentId, markdown }),
                });
                toast.success(
                  result.underExisting
                    ? t("Drafted into the “{{heading}}” section", {
                        heading: result.heading ?? "",
                      })
                    : t("Section “{{heading}}” drafted", { heading: result.heading ?? "" }),
                );
                done();
              }, t("Unable to apply changes"))
            }
          />
        ) : null}

        {proposal?.actionId === "notify" ? (
          <IntentCard
            title={proposal.summary ?? t("Publish this notification?")}
            meta={[
              proposal.notify.message ? `“${proposal.notify.message}”` : null,
              proposal.notify.audienceSummary ? `→ ${proposal.notify.audienceSummary}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            warnings={
              canBroadcast
                ? warnings
                : [
                    ...warnings,
                    t("Only a workspace admin can publish a notification to everybody."),
                  ]
            }
            primaryLabel={t("Publish")}
            // ⚠ The server gates this on authentication ALONE — any member could broadcast
            // (§E2 R3). The client gate is the only one there is, so it is not optional.
            canCreate={canCreate && canBroadcast}
            busy={busy}
            onDiscard={discard}
            onPrimary={() =>
              void run(async () => {
                await notify.mutateAsync({
                  message: proposal.notify.message ?? "",
                  ...(proposal.notify.audienceRole
                    ? { audienceRole: proposal.notify.audienceRole }
                    : {}),
                  ...(proposal.notify.documentId ? { documentId: proposal.notify.documentId } : {}),
                  ...(proposal.notify.collectionId
                    ? { collectionId: proposal.notify.collectionId }
                    : {}),
                });
                toast.success(t("Notification published"));
                done();
              }, t("Couldn't publish that notification"))
            }
          />
        ) : null}
      </div>
    </>
  );
}

/** The navigate card's question, by the KIND of the top target (S1/S4). */
function navigateCardTitle(
  kind: NavigateTargetKind | undefined,
  t: (key: string) => string,
): string {
  switch (kind) {
    case "research":
      return t("Open this research?");
    case "collection":
      return t("Open this collection?");
    case "work":
      return t("Open this work item?");
    default:
      return t("Go to this document?");
  }
}

/**
 * `create-document` needs one extra check the other cards do not.
 *
 * The server's collection lookup is team-scoped but **not ACL-scoped** (§E2 R4) — unlike the
 * digest and navigate lookups, which the same controller fixed — so a private collection's name
 * and id can come back as the pre-filled target. Slate re-validates the id against the
 * collections this viewer can actually see before it will create anything there.
 */
function CreateDocumentCard({
  proposal,
  warnings,
  busy,
  knownCollectionIds,
  onDiscard,
  onCreate,
}: {
  proposal: Extract<RouteProposal, { actionId: "create-document" }>;
  warnings: string[];
  busy: boolean;
  knownCollectionIds: string[];
  onDiscard: () => void;
  onCreate: (collectionId: string | null) => void;
}) {
  const { t } = useTranslation();
  const proposed = proposal.document.collectionId;
  const visible = proposed !== null && knownCollectionIds.includes(proposed);
  const unverified = proposed !== null && !visible;

  return (
    <IntentCard
      title={proposal.summary ?? t("Create this document?")}
      meta={[
        `“${proposal.document.title ?? ""}”`,
        proposal.document.collectionTitle
          ? `→ ${proposal.document.collectionTitle}`
          : t("in this collection"),
      ].join(" · ")}
      warnings={
        unverified
          ? [
              ...warnings,
              t("I can't confirm that collection is one you can write to — creating a draft."),
            ]
          : warnings
      }
      primaryLabel={t("Create document")}
      canCreate={proposal.missing.length === 0}
      busy={busy}
      onDiscard={onDiscard}
      onPrimary={() => onCreate(visible ? proposed : null)}
    />
  );
}
